import React from "react";
import PropTypes from "prop-types";

const STYLES = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  label: {
    fontSize: "13px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "2px",
  },
  select: {
    fontSize: "13px",
    padding: "2px 4px",
    border: "1px solid #333",
    borderRadius: "2px",
    backgroundColor: "white",
    color: "#333",
    width: "100%",
    maxWidth: "280px",
  },
  itemInfo: {
    fontSize: "13px",
    color: "#666",
    fontStyle: "italic",
    marginTop: "2px",
    lineHeight: "1.2",
  },
};

const TEXT = {
  LABEL: "Select Physical Copy:",
  COPY_OF: "Copy",
  SINGLE_COPY: "Single Copy Available",
};

const ItemPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  barcode: PropTypes.string,
  callNumber: PropTypes.string,
  location: PropTypes.string,
  status: PropTypes.string,
  source: PropTypes.string,
  itemData: PropTypes.object,
});

const ItemSelector = ({ items, selectedItemId, onItemChange }) => {
  if (!items || items.length === 0) return null;

  const selectedItem =
    items.find((item) => item.id === selectedItemId) || items[0];

  const handleSelectChange = (event) => {
    const newItemId = event.target.value;
    onItemChange(newItemId);
  };

  const renderSelectOption = (item, index) => {
    const displayText = getItemDisplayText(item, index);
    return (
      <option key={item.id} value={item.id}>
        {displayText}
      </option>
    );
  };

  const renderItemInfo = () => {
    if (!selectedItem) return null;

    const info = [];
    if (info.length === 0) return null;

    return <div style={STYLES.itemInfo}>{info.join(" • ")}</div>;
  };

  // Don't show selector if only one item
  if (items.length === 1) {
    return (
      <div style={STYLES.container}>
        <div style={STYLES.label}>{TEXT.SINGLE_COPY}</div>
        {renderItemInfo()}
      </div>
    );
  }

  return (
    <div style={STYLES.container}>
      <label style={STYLES.label} htmlFor="item-selector">
        {TEXT.LABEL}
      </label>
      <select
        id="item-selector"
        style={STYLES.select}
        value={selectedItemId || ""}
        onChange={handleSelectChange}
      >
        {items.map(renderSelectOption)}
      </select>
      {renderItemInfo()}
    </div>
  );
};

function extractCallNumberSuffix(callNumber) {
  if (!callNumber) return null;

  const suffixMatch = callNumber.match(/\bc\.?(\d+)$/i);
  if (suffixMatch) {
    return suffixMatch[1];
  }

  const copyMatch = callNumber.match(/copy\s*(\d+)$/i);
  if (copyMatch) {
    return copyMatch[1];
  }

  const spaceMatch = callNumber.match(/\s+c\.?(\d+)$/i);
  if (spaceMatch) {
    return spaceMatch[1];
  }

  return null;
}

function extractSuffixFromItemData(item) {
  if (item?.call_number_suffix) {
    const suffixMatch = item.call_number_suffix.match(/c\.?(\d+)$/i);
    if (suffixMatch) {
      return suffixMatch[1];
    }
    const numberMatch = item.call_number_suffix.match(/^\d+$/);
    if (numberMatch) {
      return item.call_number_suffix;
    }
  }

  return extractCallNumberSuffix(item?.call_number || item?.callNumber);
}

function getItemDisplayText(item, index) {
  let suffix = null;

  if (item.itemData?.call_number_suffix) {
    suffix = extractSuffixFromItemData(item.itemData);
  }

  let copyText;
  if (suffix) {
    copyText = `${TEXT.COPY_OF} ${suffix}`;
  } else {
    // Fallback to index-based numbering
    const copyNumber = index + 1;
    copyText = `${TEXT.COPY_OF} ${copyNumber}`;
  }

  const parts = [copyText];

  if (item.barcode && item.barcode !== "N/A" && item.barcode !== item.id) {
    parts.push(`(${item.barcode})`);
  } else if (item.id !== "default") {
    parts.push(`(${item.id})`);
  }

  if (item.location) {
    parts.push(`- ${item.location}`);
  }

  return parts.join(" ");
}

ItemSelector.propTypes = {
  items: PropTypes.arrayOf(ItemPropType).isRequired,
  selectedItemId: PropTypes.string,
  onItemChange: PropTypes.func.isRequired,
};

export default ItemSelector;
